import { useSitesByCompanyId } from "./data/useSitesByCompanyId";
import { useTranslation } from "react-i18next";
import { useAlternativeRegionNames } from "./data/Settings/useAlternativeRegionNames";
import { Col, Row } from "react-bootstrap";
import { Autocomplete, TextField, Typography } from "@mui/material";
import React from "react";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";
import { Region } from "./shared/interfaces/Region";

interface SiteDropdownProps {
  companyId?: number | undefined;
  customerId?: number | undefined;
  selectedSiteName?: string;
  onSiteChange: (site: string, siteId?: number) => void;
  showLabel?: boolean;
  showClear?: boolean;
  clearAuto?: boolean;
  className?: string;
  disabled?: boolean;
  width?: string;
}

export function SiteDropdown(props: SiteDropdownProps) {
  const { t } = useTranslation();
  const { companyId, customerId, selectedSiteName, onSiteChange, showLabel, showClear, clearAuto, disabled, width } =
    props;

  const altRegionNames = useAlternativeRegionNames(customerId);

  const sites = useSitesByCompanyId(companyId);

  function handleChange(e: React.SyntheticEvent, value: Region | null) {
    onSiteChange(value?.name ?? "", value?.id ?? undefined);
  }

  const siteLabelTemplate = (
    <Col xs={"auto"} className="d-flex align-items-center">
      <Typography variant="subtitle2" color="textPrimary">
        {altRegionNames.data?.siteAltName ?? t("common.site")}:
      </Typography>
    </Col>
  );

  const selectedSite = sites.data?.find((site) => site.name === selectedSiteName);

  return (
    <Row className={showLabel ? "d-flex flex-column ml-1" : ""}>
      {showLabel && siteLabelTemplate}
      <Col {...(clearAuto ? {} : { xs: "auto" })}>
        <Autocomplete
          disableClearable={!showClear}
          autoHighlight
          blurOnSelect
          disabled={companyId === undefined || disabled}
          id="siteDropdown"
          value={selectedSite ?? null}
          options={sites.data ?? []}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t("common.select", {
                item: altRegionNames.data?.siteAltName ?? "$t(common.site)",
              })}
            />
          )}
          renderOption={(props, option, { inputValue }) => {
            const matches = match(option.name, inputValue, {
              insideWords: true,
            });
            const parts = parse(option.name, matches);
            return (
              <li {...props}>
                <div>
                  {parts.map((part, index) => (
                    <span
                      key={index}
                      style={{
                        fontWeight: part.highlight ? 700 : 400,
                      }}
                    >
                      {part.text}
                    </span>
                  ))}
                </div>
              </li>
            );
          }}
          size="small"
          sx={{ width: width || 200, bgcolor: "white" }}
          onChange={handleChange}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
        />
      </Col>
    </Row>
  );
}
